import { useEffect, useState } from 'react';
import { z } from 'zod';
import { httpBoundary } from '@werkingflow/trust-boundary';
import { ACCOUNTS } from '../types';

const Katalog = z.object({ accounts: z.array(z.object({
  id: z.string(), label: z.string(), provider: z.string(),
  models: z.array(z.object({ id: z.string(), label: z.string() })),
  defaultModel: z.string(), quelle: z.string(),
})) });
type Konto = z.infer<typeof Katalog>['accounts'][number];
const stil = { flex: '1 1 140px', minWidth: 0, padding: '4px 6px', fontSize: 11, borderRadius: 6,
  background: 'var(--tn-bg)', border: '1px solid var(--tn-border)', color: 'var(--tn-text)' } as const;

/** Only a new stage's optional selection; never changes an existing session. */
export function GuideBeraterStartwahl({ betreiber, fremdesHaus, zielHaus, konto, modell, setKonto, setModell }: {
  betreiber: boolean; fremdesHaus: boolean; zielHaus: 'dev' | 'partner' | null;
  konto: string; modell: string; setKonto: (id: string) => void; setModell: (id: string) => void;
}) {
  const [offen, setOffen] = useState(false);
  const [katalog, setKatalog] = useState<Konto[] | null>(null);
  const [fehler, setFehler] = useState('');
  const [versuch, setVersuch] = useState(0);
  const echteAuswahl = betreiber && !fremdesHaus;
  useEffect(() => {
    if (!offen || !echteAuswahl) return;
    let aktiv = true;
    setFehler('');
    httpBoundary({ name: 'cui.guide.neuer-berater-modelle', url: '/api/guide/coach/assistent/steuerung', schema: Katalog, timeoutMs: 15000 })
      .then(d => { if (aktiv) setKatalog(d.accounts); })
      .catch(e => { if (aktiv) setFehler(e instanceof Error ? e.message : 'Modellkatalog konnte nicht geladen werden.'); });
    return () => { aktiv = false; };
  }, [offen, echteAuswahl, versuch]);
  const konten = echteAuswahl ? (katalog ?? []) : ACCOUNTS.filter(a => a.id !== 'local' && a.id !== 'auto' && a.provider !== 'codex');
  const gewaehlt = katalog?.find(a => a.id === konto);
  const modelle = echteAuswahl
    ? [...new Map((konto ? gewaehlt?.models ?? [] : (katalog ?? []).flatMap(a => a.models)).map(m => [m.id, m])).values()]
    : ['opus', 'sonnet', 'haiku', 'fable'].map(id => ({ id, label: id }));
  const auswahlFehlt = echteAuswahl && !katalog;
  function kontoWaehlen(id: string) {
    setKonto(id);
    if (modell && echteAuswahl && id && !katalog?.find(a => a.id === id)?.models.some(m => m.id === modell)) setModell('');
  }
  return <details data-ai-id="guide-berater-einsetzen-erweitert" style={{ marginTop: 6 }} onToggle={e => setOffen(e.currentTarget.open)}>
    <summary style={{ cursor: 'pointer', color: 'var(--tn-text-muted)' }}>Konto und Modell</summary>
    <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
      <select aria-label="Konto für neue Bühne" data-ai-id="guide-berater-einsetzen-konto" value={konto}
        disabled={fremdesHaus || auswahlFehlt} onChange={e => kontoWaehlen(e.target.value)} style={stil}>
        <option value="">{fremdesHaus ? `Konto: wählt Haus ${zielHaus === 'dev' ? 'Dev' : 'Partner'}` : 'Konto: automatisch'}</option>
        {konto && !konten.some(a => a.id === konto) && <option value={konto}>{ACCOUNTS.find(a => a.id === konto)?.label ?? konto} · bisherige Auswahl</option>}
        {konten.map(a => <option key={a.id} value={a.id}>{a.label}</option>)}
      </select>
      <select aria-label="Modell für neue Bühne" data-ai-id="guide-berater-einsetzen-modell" value={modell}
        disabled={auswahlFehlt} onChange={e => setModell(e.target.value)} style={stil}>
        <option value="">Modell: automatisch</option>
        {modell && !modelle.some(m => m.id === modell) && <option value={modell}>{modell} · bisherige Auswahl</option>}
        {modelle.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
      </select>
    </div>
    {offen && auswahlFehlt && !fehler && <p>Verfügbare Konten und Modelle werden geladen …</p>}
    {fehler && <p role="alert">Die manuelle Modellauswahl konnte nicht aktualisiert werden. Die automatische Auswahl bleibt möglich. <button type="button" onClick={() => setVersuch(v => v + 1)}>Erneut laden</button></p>}
    {gewaehlt && echteAuswahl && <small>{gewaehlt.quelle}{gewaehlt.defaultModel ? ` · Konto-Vorgabe: ${gewaehlt.defaultModel}` : ''}</small>}
  </details>;
}
